// Procesado de imagen para el análisis de movimiento (vídeos de gimnasio con
// poca luz). Dos consumidores:
//  - la VISUALIZACIÓN (opción "mejorar luz", activada por defecto): LUT sobre
//    el ImageData RGBA -> computeStretchLUT + applyLUT. Afecta a lo que se ve
//    y a lo que se graba en la corrección.
//  - el TRACKER de la barra: toGray + stretchGray sobre el frame CRUDO.
//    Independiente de la opción anterior (ver nota en stretchGray).

// Calcula una LUT (256 entradas) a partir del histograma de luminancia del
// frame: estira el rango [percentil bajo, percentil alto] a [0,255] y aplica
// gamma < 1 para levantar sombras. Submuestrea píxeles (x4) por rendimiento.
export function computeStretchLUT(imageData, lowPct = 0.01, highPct = 0.99, gamma = 0.7) {
  const { data } = imageData;
  const hist = new Uint32Array(256);
  let count = 0;
  for (let i = 0; i < data.length; i += 16) {
    const y = (data[i] * 77 + data[i + 1] * 150 + data[i + 2] * 29) >> 8;
    hist[y]++;
    count++;
  }
  const { low, range } = percentileRange(hist, count, lowPct, highPct);
  const lut = new Uint8ClampedArray(256);
  for (let v = 0; v < 256; v++) {
    const t = Math.min(1, Math.max(0, (v - low) / range));
    lut[v] = Math.round(255 * Math.pow(t, gamma));
  }
  return lut;
}

// Aplica la LUT por canal, in place.
export function applyLUT(imageData, lut) {
  const { data } = imageData;
  for (let i = 0; i < data.length; i += 4) {
    data[i] = lut[data[i]];
    data[i + 1] = lut[data[i + 1]];
    data[i + 2] = lut[data[i + 2]];
  }
}

// Devuelve el frame como gris Float32.
export function toGray(imageData) {
  const { data, width, height } = imageData;
  const gray = new Float32Array(width * height);
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    gray[p] = (data[i] * 77 + data[i + 1] * 150 + data[i + 2] * 29) >> 8;
  }
  return { data: gray, width, height };
}

// Estiramiento de contraste + gamma sobre la imagen en gris, IN PLACE.
//
// IMPORTANTE: es preprocesado INTERNO y exclusivo del tracker de la barra, y se
// aplica SIEMPRE, esté o no activada la opción "mejorar luz" de la interfaz
// (así el seguimiento no cambia de comportamiento al tocar un ajuste visual).
// En los vídeos oscuros de gimnasio el disco tiene muy poca varianza y la
// correlación NCC se vuelve inestable: medido contra un vídeo real de
// sentadilla, alimentar al tracker con el frame estirado sube la puntuación
// mínima de 0.42 a 0.72 y reduce la deriva horizontal de 59 px a 42 px.
// Es load-bearing: no quitarlo "por simplificar".
export function stretchGray(gray) {
  const { data } = gray;
  const hist = new Uint32Array(256);
  let count = 0;
  for (let p = 0; p < data.length; p += 4) {
    hist[data[p] | 0]++;
    count++;
  }
  const { low, range } = percentileRange(hist, count, 0.01, 0.99);
  const lut = new Float32Array(256);
  for (let v = 0; v < 256; v++) {
    const t = Math.min(1, Math.max(0, (v - low) / range));
    lut[v] = 255 * Math.pow(t, 0.7);
  }
  for (let p = 0; p < data.length; p++) data[p] = lut[data[p] | 0];
  return gray;
}

// Percentiles bajo/alto del histograma -> rango a estirar. Si el frame es casi
// plano se ensancha el rango para no amplificar ruido.
function percentileRange(hist, count, lowPct, highPct) {
  let low = 0;
  let high = 255;
  let acc = 0;
  const lowTarget = count * lowPct;
  const highTarget = count * highPct;
  for (let v = 0; v < 256; v++) {
    acc += hist[v];
    if (acc <= lowTarget) low = v;
    if (acc <= highTarget) high = v;
  }
  if (high - low < 16) high = Math.min(255, low + 16);
  return { low, range: high - low };
}
